import React, { useState, useEffect } from "react";
import { Search, ChevronDown } from "lucide-react";
import { getInStockProductsList } from "../../../services";

const AddProductModal = ({ isOpen, onClose, onAddProduct }) => {
  const [productData, setProductData] = useState({
    name: "",
    sellPrice: "",
    quantity: 1,
  });

  // Product dropdown states
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productLoading, setProductLoading] = useState(false);

  // Fetch products when dropdown opens
  const formatSellPriceValue = (value) => {
    if (value === "" || value === null || value === undefined) {
      return "";
    }
    const parsed = Number(value);
    if (isNaN(parsed)) {
      return "";
    }
    const rounded = Math.round((parsed + Number.EPSILON) * 100) / 100;
    return rounded.toFixed(2);
  };

  const fetchProducts = async () => {
    setProductLoading(true);
    try {
      const response = await getInStockProductsList({
        search: productSearchTerm,
        page: 1,
        page_size: 50,
      });

      let productsList = [];
      if (response) {
        if (Array.isArray(response)) {
          productsList = response;
        } else if (response.results && Array.isArray(response.results)) {
          productsList = response.results;
        } else if (response.data && Array.isArray(response.data)) {
          productsList = response.data;
        }
      }
      setProducts(productsList);
    } catch (err) {
      console.error("Error fetching products:", err);
      setProducts([]);
    } finally {
      setProductLoading(false);
    }
  };

  const handleProductSelect = (product, shouldCloseDropdown = true) => {
    setSelectedProduct(product);
    const resolvedPrice =
      product.avg_selling_price ||
      product.latest_selling_price ||
      product.sellPrice ||
      "";
    setProductData((prev) => ({
      ...prev,
      name: product.name,
      sellPrice: resolvedPrice !== "" ? formatSellPriceValue(resolvedPrice) : "",
    }));

    if (shouldCloseDropdown) {
      setIsProductDropdownOpen(false);
      setProductSearchTerm("");
    }

  };

  // Don't filter products on frontend - API already handles search
  // This allows unique_id searches to work properly
  const filteredProducts = products;

  // Helper function to format product name like ProductTable
  const formatProductName = (product) => {
    const details = [];

    if (product.ram) details.push(product.ram);
    if (product.storage) details.push(product.storage);
    if (product.variants && product.variants.length > 0) {
      const variantText = product.variants
        .map((variant) => Object.values(variant).join(", "))
        .join(", ");
      details.push(variantText);
    }
    if (product.suffix) details.push(product.suffix);
    if (product.used_mobile !== undefined && product.used_mobile !== null) {
      details.push(product.used_mobile === false ? "New" : "Used");
    }

    return {
      name: product.name,
      details: details.join(" | "),
    };
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isProductDropdownOpen &&
        !event.target.closest(".product-dropdown-container")
      ) {
        setIsProductDropdownOpen(false);
      }
    };

    if (isProductDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProductDropdownOpen]);

  // Fetch products when dropdown opens or search term changes
  useEffect(() => {
    if (isProductDropdownOpen) {
      fetchProducts();
      // Auto-focus search input when dropdown opens
      setTimeout(() => {
        const searchInput = document.querySelector('.product-dropdown-container .batches-search-input');
        if (searchInput) searchInput.focus();
      }, 100);
    }
  }, [isProductDropdownOpen, productSearchTerm]);

  // Auto-select product when unique_id is found in search results
  useEffect(() => {
    if (filteredProducts.length > 0 && productSearchTerm) {
      // Check if any product has unique_id (barcode search)
      const productWithUniqueId = filteredProducts.find(
        (product) => product.unique_id !== null && product.unique_id !== undefined
      );
      
      if (productWithUniqueId) {
        // Auto-select product with unique_id and keep it selected
        handleProductSelect(productWithUniqueId, false);
      } else if (!selectedProduct) {
        // Normal search - auto-select first product
        handleProductSelect(filteredProducts[0], false);
      }
    }
  }, [filteredProducts, productSearchTerm]);

  const handleInputChange = (field, value) => {
    // Prevent negative values for numeric fields
    if (field === "sellPrice" && value !== "" && parseFloat(value) < 0) {
      return;
    }

    setProductData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!productData.name || !productData.sellPrice) {
      alert("Please fill in all required fields");
      return;
    }

    // Use the selected product's ID if available, otherwise use a fallback
    const productId = selectedProduct ? selectedProduct.id : Date.now();

    const formattedSellPrice = parseFloat(formatSellPriceValue(productData.sellPrice));

    const newProduct = {
      id: productId,
      name: productData.name,
      buyPrice: selectedProduct?.avg_buying_price || selectedProduct?.latest_buying_price || 0, // Include buyPrice from selected product (not shown in form)
      sellPrice: formattedSellPrice,
      quantity: parseInt(productData.quantity),
      image: selectedProduct?.image || null, // Use selected product's image if available
      in_stock: selectedProduct?.in_stock || 0, // Include in_stock for stock validation
      // Include additional product details for consistency
      ram: selectedProduct?.ram || "",
      storage: selectedProduct?.storage || "",
      variants: selectedProduct?.variants || [],
      suffix: selectedProduct?.suffix || "",
      used_mobile: selectedProduct?.used_mobile,
      unique_id: selectedProduct?.unique_id || null, // Include unique_id if available
    };

    onAddProduct(newProduct);
    setProductData({ name: "", sellPrice: "", quantity: 1 });
    setSelectedProduct(null);
    setProductSearchTerm("");
    setIsProductDropdownOpen(false);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h3>Add Product</h3>
        </div>

        <div className="modal-content">
          <p>Fill in the transaction details below to add new transaction</p>

          <div className="form-group">
            <label>Product Name *</label>
            <div className="product-dropdown-container">
              <div
                className="dropdown-input"
                onClick={() => {
                  
                  setIsProductDropdownOpen(!isProductDropdownOpen);
                }}
              >
                <span className="selected-value">
                  {selectedProduct
                    ? formatProductName(selectedProduct).name
                    : productData.name || "Select product"}
                </span>
                <ChevronDown className="dropdown-arrow" size={16} />
              </div>

              {isProductDropdownOpen && (
                <div className="dropdown-menu" style={{ display: "block" }}>
                  <div className="dropdown-search">
                    <Search className="batches-search-icon" size={16} />
                    <input
                      type="text"
                      className="batches-search-input"
                      placeholder="Search product name..."
                      value={productSearchTerm}
                      onChange={(e) => setProductSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="dropdown-list noscrollbar">
                    {productLoading ? (
                      <div className="batches-dropdown-item">
                        Loading products...
                      </div>
                    ) : filteredProducts.length > 0 ? (
                      filteredProducts.map((product) => {
                        const formatted = formatProductName(product);
                        return (
                          <div
                            key={product.id}
                            className="batches-dropdown-item"
                            onClick={() => handleProductSelect(product)}
                          >
                            <div>
                              <div>{formatted.name}</div>
                              {formatted.details && (
                                <div
                                  style={{
                                    fontSize: "0.8em",
                                    color: "#666",
                                    marginTop: "4px",
                                  }}
                                >
                                  {formatted.details}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="batches-dropdown-item no-results">
                        No products found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Sell Price *</label>
              <input
                type="number"
                className="form-input"
                placeholder="€0"
                value={productData.sellPrice}
                onChange={(e) => handleInputChange("sellPrice", e.target.value)}
                onBlur={() =>
                  setProductData((prev) => ({
                    ...prev,
                    sellPrice: formatSellPriceValue(prev.sellPrice),
                  }))
                }
                min="0"
                step="0.01"
              />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSubmit}>
            Add Product
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddProductModal;
